import api from '@/services/api';
import type { Vehicle, VehicleFormData, VehicleResponse, VehiclesResponse } from '@/types/vehicle';

const API_BASE = '/customer/vehicles';

export const vehicleService = {
  /**
   * Get all vehicles for the authenticated customer
   */
  async getVehicles(): Promise<Vehicle[]> {
    const response = await api.get<VehiclesResponse>(API_BASE);
    return response.data.data || [];
  },

  /**
   * Get a single vehicle by ID
   */
  async getVehicle(vehicleId: number): Promise<Vehicle> {
    const response = await api.get<VehicleResponse>(`${API_BASE}/${vehicleId}`);
    if (!response.data.data) {
      throw new Error('Vehicle not found');
    }
    return response.data.data;
  },

  /**
   * Create a new vehicle with optional photos
   */
  async createVehicle(data: VehicleFormData, photoFiles: File[] = [], photoSides: string[] = []): Promise<Vehicle> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    // Append vehicle photos with side keys
    photoFiles.forEach((photo, index) => {
      const side = photoSides[index];
      formData.append(`vehicle_photos[${side}]`, photo);
    });

    const response = await api.post<VehicleResponse>(API_BASE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (!response.data.data) {
      throw new Error('Failed to create vehicle');
    }
    return response.data.data;
  },

  /**
   * Update an existing vehicle with optional new photos
   */
  async updateVehicle(vehicleId: number, data: VehicleFormData, photoFiles: File[] = [], photoSides: string[] = []): Promise<Vehicle> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    formData.append('_method', 'PUT');

    // Append new vehicle photos with side keys
    photoFiles.forEach((photo, index) => {
      const side = photoSides[index];
      formData.append(`vehicle_photos[${side}]`, photo);
    });

    const response = await api.post<VehicleResponse>(`${API_BASE}/${vehicleId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (!response.data.data) {
      throw new Error('Failed to update vehicle');
    }
    return response.data.data;
  },

  /**
   * Delete a vehicle
   */
  async deleteVehicle(vehicleId: number): Promise<void> {
    await api.delete(`${API_BASE}/${vehicleId}`);
  },

  /**
   * Delete a vehicle photo
   */
  async deleteVehiclePhoto(photoId: number): Promise<void> {
    await api.delete(`${API_BASE}/photos/${photoId}`);
  }
};

